import { Chess, validateFen } from 'chess.js';

/** Existing index namespaces include variant AND SPID. Unknown variants are unsafe. */
export function positionContext(context = {}) {
    const name = String(context.variant || context.gameType || (context.spid !== undefined && String(context.spid) !== '' ? 'chess960' : 'standard')).trim().toLowerCase();
    const variant = ['standard', 'normal', 'chess'].includes(name) ? 'standard'
        : ['chess960', '960', 'spid', 'fischerandom', 'fischerrandom', 'fischer-random', 'freestyle'].includes(name) ? 'chess960' : name;
    const spid = context.spid == null ? '' : String(context.spid).trim();
    const supported = variant === 'standard' ? !spid && !context.backrank
        : variant === 'chess960' && /^\d{1,3}$/.test(spid) && Number(spid) >= 1 && Number(spid) <= 960;
    return { variant, spid, supported };
}

/** Validate structure with the existing engine, without interpreting Chess960 castling.
 * File rights are retained literally, as in FenKeyUtil; this is NOT a 960 move engine.
 */
export function validatePositionFen(fen, context = {}) {
    const namespace = positionContext(context);
    if (!namespace.supported) return { ok: false, error: 'Position search requires a supported variant and Chess960 SPID.' };
    const fields = String(fen || '').trim().split(/\s+/);
    if (![4, 6].includes(fields.length)) return { ok: false, error: 'Invalid FEN' };
    if (fields.length === 4) fields.push('0', '1');
    if (namespace.variant === 'chess960') {
        const rights = fields[2];
        if (!/^(?:-|[KQkqA-Ha-h]+)$/.test(rights) || (rights !== '-' && new Set(rights).size !== rights.length))
            return { ok: false, error: 'Invalid FEN: castling availability is invalid' };
        fields[2] = '-';
    }
    return validateFen(fields.join(' '));
}

/** Mirrors FenKeyUtil.toFen4: literal rights, adjacency-based EP, ignored clocks.
 * Namespace is separate from FEN4; scopedPositionIdentity uses the public SPID
 * only for local board comparisons, never for repository lookup keys.
 */
export function positionIdentity(fen, context = {}) {
    try {
        const fields = String(fen || '').trim().split(/\s+/);
        if (!validatePositionFen(fen, context).ok) return '';
        const chess = new Chess(`${fields[0]} ${fields[1]} - ${fields[3]} 0 1`);
        let ep = fields[3];
        if (ep !== '-') {
            const file = ep.charCodeAt(0) - 97;
            const rank = Number(ep[1]) + (fields[1] === 'w' ? -1 : 1);
            const pawn = [-1, 1].some(offset => {
                const adjacent = file + offset;
                if (adjacent < 0 || adjacent > 7) return false;
                const piece = chess.get(`${String.fromCharCode(97 + adjacent)}${rank}`);
                return piece?.type === 'p' && piece.color === fields[1];
            });
            if (!pawn) ep = '-';
        }
        return `${fields[0]} ${fields[1]} ${fields[2]} ${ep}`;
    } catch (_) { return ''; }
}

export function scopedPositionIdentity(fen, context = {}) {
    const key = positionIdentity(fen, context);
    const { variant, spid } = positionContext(context);
    return key ? `${key}|v=${variant}|s=${spid}` : '';
}
